"use client";

import { useRef, useState } from "react";
import { makeFunctionReference } from "convex/server";
import { useMutation, useQuery } from "convex/react";
import { Upload } from "lucide-react";
import { QuerySkeleton } from "@/components/ui/skeleton";

interface PodUploadLinkSnapshot {
  token: string;
  expiresAt?: string;
  load: {
    reference: string;
    origin: { city: string; state: string };
    destination: { city: string; state: string };
  };
}

const getPublicUploadLink = makeFunctionReference<
  "query",
  { tenantId: string; token: string },
  PodUploadLinkSnapshot | null
>("podUploads:getPublicUploadLink");

const generateUploadUrl = makeFunctionReference<
  "mutation",
  { tenantId: string; token?: string },
  { uploadUrl: string }
>("documentStorage:generateUploadUrl");

const recordUploadedFile = makeFunctionReference<
  "mutation",
  { tenantId: string; storageId: string; fileName: string; documentId?: string; token?: string },
  { documentId: string; loadId: string; ocrScheduled: boolean }
>("documentStorage:recordUploadedFile");

type UploadState =
  | { phase: "idle" }
  | { phase: "uploading"; fileName: string }
  | { phase: "done"; fileName: string }
  | { phase: "error"; message: string };

export function PodUploadLinkView({ tenantId, token }: { tenantId: string; token: string }) {
  const snapshot = useQuery(getPublicUploadLink, { tenantId, token });
  const generateUrl = useMutation(generateUploadUrl);
  const recordUpload = useMutation(recordUploadedFile);
  const inputRef = useRef<HTMLInputElement>(null);
  const [upload, setUpload] = useState<UploadState>({ phase: "idle" });

  if (snapshot === undefined) {
    return (
      <main className="mx-auto max-w-xl px-6 py-8">
        <QuerySkeleton surface="podUpload" />
      </main>
    );
  }
  if (snapshot === null) return <PodUploadShell title="POD link unavailable" detail="This upload token is expired or invalid." />;

  async function handleFile(file?: File | null) {
    if (!file || upload.phase === "uploading") return;
    setUpload({ phase: "uploading", fileName: file.name });
    try {
      const { uploadUrl } = await generateUrl({ tenantId, token });
      const post = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!post.ok) throw new Error(`storage upload failed (${post.status})`);
      const { storageId } = (await post.json()) as { storageId: string };
      await recordUpload({ tenantId, token, storageId, fileName: file.name });
      setUpload({ phase: "done", fileName: file.name });
    } catch (error) {
      setUpload({ phase: "error", message: error instanceof Error ? error.message : String(error) });
    }
  }

  return (
    <main className="mx-auto max-w-xl space-y-4 px-6 py-8">
      <div>
        <h1 className="text-2xl font-semibold">Upload POD</h1>
        <p className="mt-1 text-sm text-muted-foreground">{snapshot.load.reference}</p>
      </div>
      <div className="rounded-md border border-border bg-card p-6 text-sm text-muted-foreground">
        {snapshot.load.origin.city}, {snapshot.load.origin.state} to {snapshot.load.destination.city},{" "}
        {snapshot.load.destination.state}
      </div>
      {upload.phase === "done" ? (
        <div className="rounded-md border border-border bg-card p-6 text-sm">
          <div className="font-medium">POD received</div>
          <div className="mt-1 text-muted-foreground">
            {upload.fileName} was uploaded for {snapshot.load.reference}. Document processing has been queued —
            you can close this page.
          </div>
        </div>
      ) : (
        <>
          <button
            className="flex min-h-32 w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-card px-3 py-6 text-sm text-muted-foreground hover:bg-muted disabled:opacity-60"
            disabled={upload.phase === "uploading"}
            onClick={() => inputRef.current?.click()}
            onDrop={(event) => {
              event.preventDefault();
              void handleFile(event.dataTransfer.files[0]);
            }}
            onDragOver={(event) => event.preventDefault()}
          >
            <Upload size={20} />
            <span>
              {upload.phase === "uploading"
                ? `Uploading ${upload.fileName}…`
                : "Tap to take a photo or choose the signed POD (PDF or image)"}
            </span>
          </button>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept="application/pdf,image/*"
            capture="environment"
            onChange={(event) => void handleFile(event.target.files?.[0])}
          />
          {upload.phase === "error" ? (
            <p className="text-sm text-destructive">Upload failed: {upload.message}. Please try again.</p>
          ) : null}
        </>
      )}
    </main>
  );
}

function PodUploadShell({ title, detail }: { title: string; detail: string }) {
  return (
    <main className="mx-auto max-w-xl space-y-4 px-6 py-8">
      <h1 className="text-2xl font-semibold">{title}</h1>
      <p className="text-sm text-muted-foreground">{detail}</p>
    </main>
  );
}
